import { Injectable } from "@angular/core";
import { ValuationProcessService } from "./_services";
import { Actions, Effect, ofType } from "@ngrx/effects";
import * as valuationProcessActions from './_actions'
import { map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { leases, propertyRevenues } from "./_examples";
import { HeadlineToEffectiveMeta, Lease, PropertyRevenue, ValuationAssumptionDeparture } from "./types";
import { ValuationVpga10Matter } from "src/app/core/valuation/_models/vpga10-matter.model";
import { ValuationNoteModel } from "src/app/core/valuation/_models/valuation-note.model";
import { Store } from "@ngrx/store";
import { selectFeature } from "./_selectors";
import { ValuationProcessState } from "./_reducer";
import { IncomeValuationProcess } from "./types/valuation-process";
import * as _ from 'lodash'
import { Location } from "@angular/common";
import { VComparable } from "./types/asset-class/comparable";
import { VPropertySubTypes } from "./types/asset-class/target-property";
import { dateFormat } from "../../utils";
import { mapLeaseAPIToModel, mapRevenueAPIToModel } from "./core/income";

@Injectable()
export class ValuationProcessEffects {
  constructor(
    private actions: Actions,
    private service: ValuationProcessService,
    private store: Store,
    private location: Location
  ) { }

  @Effect()
  requestValuationProcessData$ = this.actions.pipe(
    ofType(valuationProcessActions.loadData),
    switchMap(({ valuationId }) => {
      return this.service.fetch(valuationId).pipe(
        map(response => {
          return valuationProcessActions.dataLoaded({
            valuationProcess: {
              kind: 'income',
              data: {
                selectedConsiderations: setSelectedConsiderations(response.data.selected_ref_nums),
                comparables: setIncomeComparables(response.selectedComparables),
                leases: setLeases(response.data.leases),
                propertyRevenues: setPropertyRevenues(response.data.property_revenues),
                valuationLeaseType: 'capitalisation-method',
                comparableHeadlineToEffectives: setComparableHeadlineToEffective(response.data.comparable_headline_to_effectives),
                valuationDate: response.data.income_valuation && response.data.income_valuation.valuation_date
                  ? new Date(response.data.income_valuation.valuation_date)
                  : null
              }
            },
            targetProperty: response.targetProperty,
            valuation: response.valuation,
            additional: response.additional,
            assumptionDepartures: setAssumptionDepartures(response.assumptionDepartures),
            vpga10Matters: setVpga10Matters(response.vpga10Matters),
            sers: response.sers,
            notes: setNotes(response.notes),
            propertySubTypeIds: response.propertySubTypeIds,
            fullComparables: response.selectedComparables
          })
        })
      )
    })
  )

  @Effect()
  loadComparables$ = this.actions.pipe(
    ofType(valuationProcessActions.loadComparables),
    switchMap(({ filter }) => {
      return this.service.loadComparables(filter).pipe(
        map(response => {
          return valuationProcessActions.comparablesLoaded({
            items: response
          })
        })
      )
    })
  )

  @Effect()
  loadFullComparables$ = this.actions.pipe(
    ofType(valuationProcessActions.loadFullComparables),
    switchMap(({ refNums }) => {
      return this.service.loadFullComparables(refNums).pipe(
        map(response => {
          return valuationProcessActions.fullComparablesLoaded({
            items: response
          })
        })
      )
    })
  )

  @Effect()
  addedComparableRequested$ = this.actions.pipe(
    ofType(valuationProcessActions.addedComparableRequested),
    switchMap(({refNum}) => {
      return this.service.loadFullComparables([refNum]).pipe(
        map(response => {
          return valuationProcessActions.addedComparableLoaded({
            item: response[0]
          })
        })
      )
    })
  )

  @Effect()
  storeData$ = this.actions.pipe(
    ofType(valuationProcessActions.storeData),
    withLatestFrom(this.store.select(selectFeature)),
    switchMap(([payload, state]) => {
      return this.service.storeData(
        state.valuation.valuationId,
        payload.isComplete,
        setStorePayload(state)
      ).pipe(
        map(response => {
          this.location.back()
          return valuationProcessActions.dataStored({ success: true })
        })
      )
    })
  )
}

function setPropertyRevenues(data: any[]): PropertyRevenue[] {
  return data.map(item => {
    return mapRevenueAPIToModel(item)
  })
}

function setLeases(data: any[]): Lease[] {
  return data.map(item => {
    return mapLeaseAPIToModel(item)
  })
}


function setComparableHeadlineToEffective(data: any[]): {refNum: string, meta: HeadlineToEffectiveMeta}[] {
  return data.map(item => {
    return {
      refNum: item.ref_num,
      meta: {
        mainMethod: item.main_method,
        subMethod: item.sub_method,
        targetRate: item.target_rate,
        targetRateJustification: item.target_rate_justification,
        capitalRate: item.capital_rate,
        capitalRateJustification: item.capital_rate_justification,
        growthRate: item.growth_rate,
        growthRateJustification: item.growth_rate_justification,
        effectiveRentJustification: item.effective_rent_justification,
        main: item.main == 1,
        index: item.index
      }
    }
  })
}

function setSelectedConsiderations(selecteds: {ref_num: string, selected_tenure_id: number}[]): {refNum: string, id: number}[] {
  return selecteds.filter(item => item.selected_tenure_id !== null).map(item => ({
    refNum: item.ref_num,
    id: item.selected_tenure_id
  }))
}

function setIncomeComparables(comparables: VComparable[]|null): Record<VPropertySubTypes, VComparable[]> {
  return comparables.reduce((acc, com) => { 
    return {
      ...acc,
      [com.propertySubType]: [...acc[com.propertySubType], com]
    }
  },
    {
      'Apartment': [],
      'House': [],
      'Land': [],
      'Office': [],
      "Retail Building": [],
      "Retail Shop": [],
      'Parking': [],
      'Warehouse': [],
      'Special Purpose': []
    }
  )
}

function setStorePayload(state: ValuationProcessState) {
  const leases = state.valuationProcess.kind === 'income'
    ? state.valuationProcess.data.leases
    : []
  const propertyRevenues = state.valuationProcess.kind === 'income'
    ? state.valuationProcess.data.propertyRevenues
    : []
  const selectedConsiderations = state.valuationProcess.kind === 'income'
    ? state.valuationProcess.data.selectedConsiderations
    : []
  const selectedComparables = state.valuationProcess.kind === 'income'
    ? getComparables(state.valuationProcess)
    : []

  const notes = []
  _.each(state.notes.entities, note => notes.push(note))
  const defaults = []
  _.each(state.assumptionDeparture.default.entities, el => defaults.push(el))
  const special = []
  _.each(state.assumptionDeparture.special.entities, el => special.push(el))
  const departures = []
  _.each(state.assumptionDeparture.departure.entities, el => departures.push(el))
  const vpga10 = []
  _.each(state.vpga10Matters.entities, el => vpga10.push(el))
  const srer = []
  _.each(state.srer.entities, el => srer.push(el))
  return {
    targetPropertyId: state.targetProperty.id,
    notes: notes,
    leases: leases
      .map(item => ({
        ...item, 
        startDate: dateFormat(item.startDate), 
        subLease: item.subLease
          ? {...item.subLease, startDate: dateFormat(item.subLease.startDate)}
          : null
      })),
    propertyRevenues: propertyRevenues
      .map(item => ({
        ...item, 
        startDate: dateFormat(item.startDate),
        subLease: item.subLease
          ? {...item.subLease, startDate: dateFormat(item.subLease.startDate)}
          : null
      })),
    valuationDate: state.valuationProcess.kind === 'income'
      ? dateFormat(state.valuationProcess.data.valuationDate)
      : null,
    selectedConsiderations,
    assumptionTabData: {
      assumptionDeparture: [...defaults, ...special, ...departures],
      vpga10Matters: vpga10,
      sers: srer
    },
    propertySubTypes: state.selectedPropertySubTypes,
    refNums: selectedComparables.map(item => item.refNum),
    comparableHeadlineEffectives: state.valuationProcess.kind === 'income'
      ? state.valuationProcess.data.comparableHeadlineToEffectives
      : []
  }
}

function getComparables(process: IncomeValuationProcess) {
  return _.flatten(Object.values(process.data.comparables))
}

export function setAssumptionDepartures(payload: any) {
  let def_id = 0;
  const defaultAssumptions: ValuationAssumptionDeparture[] = [];
  payload.default_assumptions.forEach(a => {
    const _new = Object.assign({}, a) as ValuationAssumptionDeparture;
    _new.front_id = def_id;
    def_id++;
    defaultAssumptions.push(_new);
  })

  let sp_id = 0;
  const specialAssumptions: ValuationAssumptionDeparture[] = [];
  payload.special_assumptions.forEach(a => {
    const _new = Object.assign({}, a) as ValuationAssumptionDeparture;
    _new.front_id = sp_id;
    sp_id++;
    specialAssumptions.push(_new);
  })

  let dp_id = 0;
  const departures: ValuationAssumptionDeparture[] = [];
  payload.departures.forEach(a => {
    const _new = Object.assign({}, a) as ValuationAssumptionDeparture;
    _new.front_id = dp_id;
    dp_id++;
    departures.push(_new);
  })
  return {
    default: defaultAssumptions,
    def_id: def_id - 1,
    special: specialAssumptions,
    sp_id: sp_id - 1,
    departure: departures,
    dp_id: dp_id - 1
  };
}

export function setVpga10Matters(payload: any) {
  let id = 0;
  const vpga10Matters: ValuationVpga10Matter[] = [];
  payload.forEach(item => {
    const _new = Object.assign({}, item) as ValuationVpga10Matter;
    _new.front_id = id;
    id++;
    vpga10Matters.push(_new);
  });

  return {
    data: vpga10Matters,
    id: id - 1
  };
}

function setNotes(payload: any) {
  let id = 0;
  const data: ValuationNoteModel[] = [];
  payload.forEach(item => {
    const _new = Object.assign({}, item) as ValuationNoteModel;
    _new.front_id = id;
    _new.status = item.status == 1
    id++;
    data.push(_new);
  });
  return {
    data,
    lastCreateId: id - 1
  };
}